import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { PdfModel } from "./content";

export interface RenderOptions {
  logoDataUrl?: string;
}

const TEAL = "#3B7A6A";
const AMBER = "#B8860B";
const RED = "#8B3A3A";
const INK = "#1a1a1a";
const MUTED = "#555555";

function determinationColor(level: PdfModel["determination"]["level"]): string {
  if (level === "eligible") return TEAL;
  if (level === "likely_eligible") return AMBER;
  return RED;
}

export function renderPdf(model: PdfModel, options: RenderOptions = {}): Blob {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const margin = 54;
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // Header
  if (options.logoDataUrl) {
    try {
      doc.addImage(options.logoDataUrl, "PNG", margin, y, 120, 40);
    } catch {
      // ignore a bad/missing logo; the document still renders
    }
  }
  doc.setFont("times", "bold");
  doc.setFontSize(18);
  doc.setTextColor(TEAL);
  doc.text(model.title, margin, y + 62);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(MUTED);
  doc.text(`${model.conditionName}  |  ${model.assessmentDate}`, margin, y + 78);
  y += 100;

  // Determination block
  const color = determinationColor(model.determination.level);
  doc.setDrawColor(color);
  doc.setLineWidth(1.5);
  doc.roundedRect(margin, y, contentWidth, 48, 4, 4);
  doc.setFont("times", "bold");
  doc.setFontSize(14);
  doc.setTextColor(color);
  doc.text(model.determination.title, margin + 12, y + 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(MUTED);
  doc.text(`Assessment score: ${model.determination.score}/100`, margin + 12, y + 38);
  y += 66;

  if (model.functionalStatus) {
    doc.setTextColor(INK);
    doc.text(model.functionalStatus, margin, y);
    y += 18;
  }

  // Referral details (professional path). Present only when captured.
  if (model.professional && model.professional.length > 0) {
    doc.setFont("times", "bold");
    doc.setFontSize(12);
    doc.setTextColor(TEAL);
    doc.text("Referral details", margin, y);
    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(INK);
    for (const row of model.professional) {
      const lines = doc.splitTextToSize(`${row.label}: ${row.value}`, contentWidth);
      doc.text(lines, margin, y);
      y += lines.length * 13 + 2;
    }
    y += 8;
  }

  // Criteria table
  if (model.criteria.length > 0) {
    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [["Status", "Criterion"]],
      body: model.criteria.map((c) => [
        c.status === "met" ? "Met" : "Not met",
        c.detail ? `${c.label}\n${c.detail}` : c.label,
      ]),
      headStyles: { fillColor: TEAL, textColor: "#ffffff" },
      styles: { fontSize: 9, cellPadding: 5 },
      columnStyles: { 0: { cellWidth: 70 } },
    });
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 18;
  }

  // Explanation
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(INK);
  const explanationLines = doc.splitTextToSize(model.explanation, contentWidth);
  doc.text(explanationLines, margin, y);
  y += explanationLines.length * 13 + 10;

  // Recommendations
  if (model.recommendations.length > 0) {
    doc.setFont("times", "bold");
    doc.setFontSize(12);
    doc.setTextColor(TEAL);
    doc.text("Recommended Next Steps", margin, y);
    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(INK);
    for (const rec of model.recommendations) {
      const lines = doc.splitTextToSize(`•  ${rec}`, contentWidth);
      doc.text(lines, margin, y);
      y += lines.length * 13 + 2;
    }
    y += 8;
  }

  // Responses table
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["Question", "Response"]],
    body: model.responses.map((r) => [r.question, r.answer]),
    headStyles: { fillColor: TEAL, textColor: "#ffffff" },
    styles: { fontSize: 9, cellPadding: 5 },
  });
  y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 18;

  // Disclaimer
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(MUTED);
  const disclaimerLines = doc.splitTextToSize(model.disclaimer, contentWidth);
  doc.text(disclaimerLines, margin, y);

  // Footer: page numbers + Confidential on every page
  const pageCount = doc.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.getHeight();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(MUTED);
    doc.text("Confidential", margin, pageHeight - 24);
    doc.text(`Page ${p} of ${pageCount}`, pageWidth - margin, pageHeight - 24, { align: "right" });
  }

  return doc.output("blob");
}
